import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore'; 
import { db, appId } from '../firebase/config';
import { useTheme } from '../context/ThemeContext';
import { useEditor } from '../context/EditorContext';
import EditableElement from '../components/editor/EditableElement';

// --- ZIG-ZAG SUAVE (8px, ARREDONDADO) ---
const SawtoothDivider = ({ position = 'top', color = '#ffffff' }) => {
    const height = "8px"; 
    const patternId = `sawtooth-home-round-${position}`;

    return (
        <div 
            className="absolute left-0 w-full overflow-hidden leading-[0] z-20"
            style={{ 
                height: height,
                top: position === 'top' ? '-7px' : 'auto',
                bottom: position === 'bottom' ? '-7px' : 'auto',
                transform: position === 'bottom' ? 'rotate(180deg)' : 'none'
            }}
        >
            <svg width="100%" height="100%">
                <defs>
                    <pattern id={patternId} x="0" y="0" width="20" height="8" patternUnits="userSpaceOnUse">
                        <path 
                            d="M0,8 L10,0 L20,8" 
                            fill={color} 
                            stroke={color} 
                            strokeWidth="2" 
                            strokeLinejoin="round" 
                        />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill={`url(#${patternId})`} />
            </svg>
        </div>
    );
};

const HomePage = ({ setCurrentPage }) => {
    const { isEditMode, themeData: draftTheme } = useEditor();
    const { theme: dbTheme } = useTheme();
    const theme = isEditMode ? { ...dbTheme, ...draftTheme } : dbTheme;

    const [content, setContent] = useState({});

    // Fontes
    const titleFont = '"Playfair Display", serif';
    const bodyFont = '"Open Sans", sans-serif';
    const scriptFont = '"Herr Von Muellerhoff", cursive';

    useEffect(() => {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Herr+Von+Muellerhoff&family=Playfair+Display:ital,wght@0,300;0,400;0,700;1,400&family=Open+Sans:wght@400;600&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        const docRef = doc(db, `artifacts/${appId}/public/data/paginas`, isEditMode ? 'home_draft' : 'home');
        const unsub = onSnapshot(docRef, (snap) => {
            if (snap.exists()) setContent(snap.data());
        });
        return () => unsub();
    }, [isEditMode]);

    return (
        <div className="bg-white">
            
            {/* --- HERO PRINCIPAL --- */}
            <div className="relative h-[85vh] min-h-[550px] bg-fixed bg-center bg-cover flex items-center justify-center"
                 style={{ backgroundImage: `url(${theme.hero_image_url || 'https://www.vintagepraia.pt/wp-content/uploads/2018/12/sushi01-1-1.jpg'})` }}>
                <div className="absolute inset-0 bg-black/50"></div>
                
                <div className="relative z-10 text-center text-white px-4 pt-16">
                    <EditableElement section="paginas/home" field="heroSubtitle" stylePrefix="heroSub" label="Texto Manuscrito">
                        <span className="text-5xl md:text-7xl text-red-600 opacity-90 block mb-2 drop-shadow-xl transform -rotate-2 select-none" style={{ fontFamily: scriptFont }}>
                            {content.heroSubtitle || "Restaurante & Sushi"}
                        </span>
                    </EditableElement>

                    <EditableElement section="paginas/home" field="heroTitle" stylePrefix="heroTitle" label="Título Principal">
                        <h1 className="text-4xl md:text-6xl font-light shadow-text uppercase tracking-[0.2em]" style={{ fontFamily: titleFont }}>
                            {content.heroTitle || "Vintage Praia"}
                        </h1>
                    </EditableElement>

                    {/* BOTÕES DE AÇÃO */}
                    <div className="mt-10 flex flex-col items-center gap-6 animate-fade-in-up">
                        
                        {/* Linha 1: Botões Principais */}
                        <div className="flex flex-col md:flex-row gap-5">
                            <button 
                                onClick={() => setCurrentPage('reservations')}
                                className="px-8 py-3 border border-white text-white hover:bg-white hover:text-black transition-all uppercase tracking-widest text-xs font-bold shadow-lg"
                            >
                                Reservar Mesa
                            </button>
                            <button 
                                onClick={() => setCurrentPage('menu')}
                                className="px-8 py-3 bg-amber-600 text-white hover:bg-amber-700 transition-all uppercase tracking-widest text-xs font-bold border border-amber-600 shadow-lg"
                            >
                                Ver Ementa
                            </button>
                        </div>

                        {/* Linha 2: Botão 3D (NOVO) */}
                        <a 
                            href="https://maps.app.goo.gl/RM3AV8HYYSGRBiy58" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="group flex items-center gap-3 px-6 py-2 text-white/90 hover:text-amber-400 transition-all uppercase tracking-widest text-xs font-bold border-b border-white/20 hover:border-amber-400 pb-1"
                        >
                            <i className="fas fa-cube text-lg group-hover:scale-110 transition-transform duration-300"></i>
                            <span>Visita Virtual 3D</span>
                        </a>

                    </div>
                </div>

                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white animate-bounce opacity-60">
                    <i className="fas fa-chevron-down text-xl"></i>
                </div>
            </div>

            {/* --- INTRODUÇÃO --- */}
            <div className="relative w-full bg-white text-gray-800">
                <SawtoothDivider position="top" color="#ffffff" />
                
                <div className="max-w-3xl mx-auto px-6 py-16 md:py-24 text-center">
                    <span className="text-4xl md:text-6xl text-amber-600 block mb-2 transform -rotate-2" style={{ fontFamily: scriptFont }}>
                        Descubra
                    </span>
                    
                    <EditableElement section="paginas/home" field="introTitle" stylePrefix="introTitle" label="Título Intro">
                        <h2 className="text-2xl md:text-4xl font-light uppercase tracking-[0.15em] mb-6 text-gray-900" style={{ fontFamily: titleFont }}>
                            {content.introTitle || "A Nossa História"}
                        </h2>
                    </EditableElement>
                    
                    <div className="w-16 h-px bg-amber-500 mx-auto mb-8"></div>

                    <EditableElement section="paginas/home" field="introText" stylePrefix="introText" type="longtext" label="Texto Intro">
                        <p className="text-base md:text-lg text-gray-600 leading-relaxed font-light" style={{ fontFamily: bodyFont }}>
                            {content.introText || "Situado na primeira linha de mar, o Vintage Praia combina a frescura do oceano com a arte milenar do sushi. Um espaço onde cada prato conta uma história."}
                        </p>
                    </EditableElement>

                    <div className="mt-8">
                        <img src="https://www.vintagepraia.pt/wp-content/uploads/2026/01/logo-vintage-praia-fundos-claros-2025.png" alt="Assinatura" className="h-12 mx-auto opacity-100" />
                    </div>
                </div>
                
                <SawtoothDivider position="bottom" color="#ffffff" />
            </div>

            {/* --- PARALLAX 1 (Ingredientes) --- */}
            <div className="relative h-[300px] bg-fixed bg-center bg-cover flex items-center justify-center"
                 style={{ backgroundImage: `url(${theme.home_parallax_1 || 'https://www.vintagepraia.pt/wp-content/uploads/2018/12/sushi-pieces-vintage-praia1.jpg'})` }}>
                <div className="absolute inset-0 bg-black/40"></div>
                
                <div className="relative z-10 text-center text-white px-4">
                    <span className="text-5xl md:text-7xl text-red-600 opacity-90 block mb-1 drop-shadow-xl transform -rotate-2" style={{ fontFamily: scriptFont }}>
                        Ingredientes
                    </span>
                    <h2 className="text-3xl md:text-5xl font-light shadow-text uppercase tracking-widest" style={{ fontFamily: titleFont }}>
                        Frescos & Locais
                    </h2>
                </div>
            </div>

            {/* --- DESTAQUES --- */}
            <div className="relative w-full bg-white text-gray-800">
                <SawtoothDivider position="top" color="#ffffff" />
                
                <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
                        
                        <div className="group cursor-pointer" onClick={() => setCurrentPage('menu')}>
                            <div className="overflow-hidden rounded-full w-40 h-40 mx-auto mb-5 border-4 border-gray-100 shadow-md group-hover:border-amber-100 transition-colors">
                                <img src="https://www.vintagepraia.pt/wp-content/uploads/2019/01/dsc_2786.jpg" alt="Sushi" className="w-full h-full object-cover transform group-hover:scale-110 transition-duration-500" />
                            </div>
                            <h3 className="text-xl font-light uppercase tracking-widest mb-2 group-hover:text-amber-600 transition-colors" style={{ fontFamily: titleFont }}>Sushi Bar</h3>
                            <p className="text-sm text-gray-500 font-light px-2">Peixe fresco, preparado com a mestria tradicional japonesa.</p>
                        </div>

                        <div className="group cursor-pointer" onClick={() => setCurrentPage('wine_menu')}>
                            <div className="overflow-hidden rounded-full w-40 h-40 mx-auto mb-5 border-4 border-gray-100 shadow-md group-hover:border-amber-100 transition-colors">
                                <img src="https://www.vintagepraia.pt/wp-content/uploads/2019/01/dsc_2784.jpg" alt="Vinhos" className="w-full h-full object-cover transform group-hover:scale-110 transition-duration-500" />
                            </div>
                            <h3 className="text-xl font-light uppercase tracking-widest mb-2 group-hover:text-amber-600 transition-colors" style={{ fontFamily: titleFont }}>Vinhos</h3>
                            <p className="text-sm text-gray-500 font-light px-2">Uma carta selecionada para harmonizar com a ementa.</p>
                        </div>

                        <div className="group cursor-pointer" onClick={() => setCurrentPage('events')}>
                            <div className="overflow-hidden rounded-full w-40 h-40 mx-auto mb-5 border-4 border-gray-100 shadow-md group-hover:border-amber-100 transition-colors">
                                <img src="https://www.vintagepraia.pt/wp-content/uploads/2019/06/dsc_4605-hdr.jpg" alt="Ambiente" className="w-full h-full object-cover transform group-hover:scale-110 transition-duration-500" />
                            </div>
                            <h3 className="text-xl font-light uppercase tracking-widest mb-2 group-hover:text-amber-600 transition-colors" style={{ fontFamily: titleFont }}>Ambiente</h3>
                            <p className="text-sm text-gray-500 font-light px-2">Vista mar única num ambiente acolhedor e sofisticado.</p>
                        </div>

                    </div>
                </div>
                
                <SawtoothDivider position="bottom" color="#ffffff" />
            </div>

            {/* --- RESERVAS --- */}
            <div className="relative h-[400px] bg-fixed bg-center bg-cover flex items-center justify-center"
                 style={{ backgroundImage: `url(${theme.home_parallax_2 || 'https://www.vintagepraia.pt/wp-content/uploads/2018/12/sushi01-1.jpg'})` }}>
                <div className="absolute inset-0 bg-black/50"></div>
                
                <div className="relative z-10 text-center text-white px-4">
                    <span className="text-5xl md:text-8xl text-amber-500 opacity-90 block mb-2 drop-shadow-xl transform -rotate-2" style={{ fontFamily: scriptFont }}>
                        Reservas
                    </span>
                    <h2 className="text-2xl md:text-4xl font-light shadow-text uppercase tracking-widest mb-6" style={{ fontFamily: titleFont }}>
                        Garanta o seu Lugar
                    </h2>
                    
                    <button 
                        onClick={() => setCurrentPage('reservations')}
                        className="px-8 py-3 bg-white text-black hover:bg-amber-600 hover:text-white transition-all uppercase tracking-widest text-xs font-bold shadow-lg"
                    >
                        Fazer Reserva
                    </button>
                </div>
            </div>

        </div>
    );
};

export default HomePage;